// Executive Dashboard shell — auth-gated layout + tab switcher only.
// Section bodies live in /components/executive/sections.
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Toaster } from 'sonner';
import { useAuth, getAuthHeaders } from '../contexts/AuthContext';
import CampaignWorkspace from '../components/CampaignWorkspace';
import KnowledgeBase from '../components/KnowledgeBase';
import AIChatWidget from '../components/AIChatWidget';
import OnboardingWizard from '../components/OnboardingWizard';

import { API } from '../components/executive/execShared';
import SideNav from '../components/executive/SideNav';
import ProfileSection from '../components/executive/sections/ProfileSection';
import CampaignsSection from '../components/executive/sections/CampaignsSection';
import ApplicationsSection from '../components/executive/sections/ApplicationsSection';
import MyCampaignsSection from '../components/executive/sections/MyCampaignsSection';
import EarningsSection from '../components/executive/sections/EarningsSection';
import EngagementsSection from '../components/executive/sections/EngagementsSection';
import AvailabilitySection from '../components/executive/sections/AvailabilitySection';
import ExecCalendarSection from '../components/executive/sections/ExecCalendarSection';
import ReferralSection from '../components/executive/sections/ReferralSection';

export default function ExecutiveDashboard() {
  const { user } = useAuth();
  const [active, setActive] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'profile';
  });
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/users/me/onboarding-status`, { headers: getAuthHeaders() })
      .then(r => { if (r.data?.should_show_wizard) setShowOnboarding(true); })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('tab');
    if (t) setActive(t);
  }, []);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#090E33]" data-testid="executive-dashboard">
      <Toaster position="top-right" theme="dark" richColors />
      <AIChatWidget audience="executive" getHeaders={getAuthHeaders} contactEmail={user?.email} />
      {showOnboarding && (
        <OnboardingWizard user={user} onComplete={() => setShowOnboarding(false)} onSkip={() => setShowOnboarding(false)} />
      )}
      <div className="border-b border-white/10 bg-[#111827]/50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-6">
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Executive <span className="text-[#FF4343]">Dashboard</span></h1>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-8">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
          <SideNav active={active} setActive={setActive} />
          <div className="flex-1">
            {active === 'profile' && <ProfileSection user={user} />}
            {active === 'workspace' && <CampaignWorkspace />}
            {active === 'my-campaigns' && <MyCampaignsSection />}
            {active === 'campaigns' && <CampaignsSection />}
            {active === 'applications' && <ApplicationsSection />}
            {active === 'earnings' && <EarningsSection />}
            {active === 'engagements' && <EngagementsSection />}
            {active === 'calendar' && <ExecCalendarSection />}
            {active === 'availability' && <AvailabilitySection />}
            {active === 'referrals' && <ReferralSection />}
            {active === 'help' && <KnowledgeBase audience="executive" />}
          </div>
        </div>
      </div>
    </div>
  );
}
